<template>

    <div class="p-2 md:p-6">
        <h2 class="font-bold uppercase tracking-wider"> {{ $t("options.galleryTitle") }} | {{ values.set.title }}</h2>

        <secondary-button @click="showGallery(values.set.images, values.set.title, values.set.description, '', '', true)" class="border-0 pl-0 mb-2">
            <InformationCircleIcon class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkbeige" />
            {{ $t("options.showInfo") }}
        </secondary-button>

        <vueper-slides
            class="no-shadow"
            style="background-image:url('/images/gallery_pattern.png')"
            fixed-height="344px"
            :visible-slides="4"
            :slideMultiple="4"
            :touchable="false"
            :initSlide=1
            :disableArrowsOnEdges="true"
            :bullets="false"
            :slide-ratio="1 / 3"
            :breakpoints="{ 800: { visibleSlides: 1, slideMultiple: 1, touchable:true, arrowsOutside: false } }"
            :arrows-outside="true">
            <vueper-slide v-for="option in values.options" :key="option.uuid">
                <template #content>
                    <div
                        :class="[option.state === 'inactive' ? 'opacity-50' : '', isSelected(option.uuid) ? 'shadow-md shadow-gray-300' : '', 'flex flex-col text-center bg-white rounded-lg shadow divide-y divide-gray-200 mx-3 my-2']">
                        <div class="flex-1 flex flex-col p-6">
                            <a class="block relative group" href="#" @click.prevent="showGallery(option.images, option.title, option.description, option.unit, option.cost, false)">
                                <span v-if="option.defaultImageLink" class="absolute z-10 inline-flex bottom-0 left-0 ml-1 mb-1 bg-minplan-lightgreybeige/50 rounded px-2 py-1">
                                     <CameraIcon class="h-4 w-4 text-white mr-1" /> <span class="text-xs text-white">{{ option.imageCount }}</span>
                                </span>
                                <div class="w-full h-32 bg-gray-100 rounded border border-gray-200 overflow-hidden relative">
                                    <img v-if="option.defaultImageLink"
                                        :class="['w-full h-full flex-shrink-0 mx-auto', option.is_croped === 'Yes' ? '' : 'object-contain']"
                                        :src="option.defaultImageLink" alt="" />
                                    <img v-if="!option.defaultImageLink"
                                        class="w-full h-full flex-shrink-0 object-contain mx-auto"
                                        src="/images/noimg.png" alt="" />
                                    
                                    <div class="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                                        <EyeIcon class="h-8 w-8 text-white opacity-80" />
                                    </div>
                                </div>
                            </a>
                            <h3 class="mt-6 text-gray-900 text-sm font-semibold text-right">{{ option.title }}</h3>
                            <dl class="mt-1 flex-grow flex flex-col justify-between">
                                <dt class="sr-only">{{ option.title }}</dt>
                                <dd class="text-gray-500 text-sm text-right h-16 max-h-16 text-ellipsis overflow-hidden">
                                    {{ option.description }}
                                </dd>
                            </dl>
                        </div>
                        <div>
                            <div class="-mt-px flex divide-x divide-gray-200 min-h-[36px]">
                                <div class="w-1/3 flex justify-start items-center text-gray-500 text-xs px-2 py-1">
                                    {{ option.cost }} <span v-if="option.unit" class="mx-1">/</span> {{ option.unit }}
                                </div>
                                <div class="-ml-px w-2/3 flex justify-end items-center px-2 py-1">
                                    <card-button
                                        @click="select(option.uuid)"
                                        v-if="!isSelected(option.uuid) && !disabled && (option.state === 'active')"
                                        class="hover:text-green-400">
                                        <span>{{ $t("actions.select") }}</span>
                                        <PlusSmIcon class="h-5 w-5 text-grey-400 ml-1" />
                                    </card-button>
                                    <card-button
                                        @click="clear()"
                                        v-if="isSelected(option.uuid) && !disabled && (option.state === 'active')"
                                        class="text-gray-400 hover:text-red-700">
                                        {{ $t("actions.reset") }}
                                        <CheckCircleIcon class="h-5 w-5 text-green-400 ml-1" />
                                    </card-button>
                                    <card-button
                                        v-if="option.state !== 'active'"
                                        class="text-gray-400 hover:text-red-400">
                                        {{ $t("actions.notAvailable") }}
                                        <XCircleIcon class="h-5 w-5 text-red-400 ml-1" />
                                    </card-button>
                                </div>
                            </div>
                        </div>
                    </div>

                </template>
            </vueper-slide>
        </vueper-slides>

        <dialog-modal :show="showGalleryDetail" maxWidth="5xl">
            <template #title>{{ values.set.title }}</template>
            <template #content>
                <div class="flex flex-col sm:flex-row items-stretch h-[320px] sm:h-[440px]">
                    <div class="w-full sm:w-3/4 h-[320px] sm:h-[440px]">

                        <vueper-slides fade fixed-height="100%">
                            <vueper-slide v-for="image in currentGalleryImages" :key="typeof image.link === 'string' ? image.link : image">
                                <template #content>
                                    <div class="w-full h-full relative group bg-gray-100">
                                        <a :href="typeof image.link === 'string' ? image.link : image" data-fancybox="modal-gallery" class="block w-full h-full">
                                            <img :src="typeof image.link === 'string' ? image.link : image" alt="" :class="['w-full h-full mx-auto', image.is_croped === 'Yes' ? '' : 'object-contain']" />
                                            <div class="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                                                <EyeIcon class="h-12 w-12 text-white opacity-90" />
                                            </div>
                                        </a>
                                    </div>
                                </template>
                            </vueper-slide>
                        </vueper-slides>

                    </div>
                    <div class="w-full sm:w-1/4 text-right mt-3 sm:mt-0">
                        <h2 class="text-xl font-bold">{{ currentGalleryTitle }}</h2>
                        <p class="text-gray-500 text-sm pl-2">{{ currentGalleryDescription }}</p>
                        <p class="text-gray-500 text-sm">{{ $filters.currencyCHF(currentGalleryCost) }} <span v-if="currentGalleryCostUnit">/</span> {{ currentGalleryCostUnit }}</p>
                    </div>
                </div>
            </template>
            <template #footer>
                <primary-button type="button" @click="hideGallery()">{{ $t("actions.close") }}</primary-button>
            </template>
        </dialog-modal>

    </div>

</template>

<script>
import CardButton from "@/Jetstream/CardButton";
import { CheckCircleIcon, PlusSmIcon, XCircleIcon, CameraIcon, InformationCircleIcon, EyeIcon } from "@heroicons/vue/outline";
import { defineComponent } from "vue";
import { VueperSlide, VueperSlides } from "vueperslides";
import "vueperslides/dist/vueperslides.css";
import DialogModal from "@/Jetstream/DialogModal";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import { Link } from "@inertiajs/inertia-vue3";

export default defineComponent({
    components: {
        DialogModal,
        CardButton,
        CheckCircleIcon,
        InformationCircleIcon,
        PlusSmIcon,
        XCircleIcon,
        CameraIcon,
        VueperSlide,
        VueperSlides,
        PrimaryButton,
        SecondaryButton,
        Link,
        EyeIcon
    },
    props: ["values", "topic", "disabled", "routeForSelect", "routeForClear"],
    data() {
        return {
            showGalleryDetail: false,
            currentGalleryImages: [],
            currentGalleryTitle: '',
            currentGalleryDescription: '',
            currentGalleryCostUnit: '',
            currentGalleryCost: '',
            isInfoGallery: false,
        };
    },
    methods: {
        isSelected(uuid) {
            return uuid === this.values.selected_option_uuid;
        },
        select(uuid) {
            axios.put(this.route(this.routeForSelect, { topic: this.topic.uuid, optionSet: this.values.set.uuid }), {
                option_uuid: uuid
            })
                .then(_ => {
                    this.values.selected_option_uuid = uuid;
                });
        },
        clear() {
            axios.put(this.route(this.routeForClear, { topic: this.topic.uuid, optionSet: this.values.set.uuid }))
                .then(_ => {
                    this.values.selected_option_uuid = null;
                });
        },
        showGallery(images, title, description, unit, cost, isInfoGallery) {
            if (images.length <= 0 && !isInfoGallery) {
                return;
            }
            this.showGalleryDetail = true;
            this.currentGalleryImages = images;
            this.currentGalleryTitle = title;
            this.currentGalleryDescription = description;
            this.currentGalleryCostUnit = unit;
            this.currentGalleryCost = cost;
        },
        hideGallery() {
            this.showGalleryDetail = false;
            this.currentGalleryImages = [];
            this.currentGalleryTitle = '';
            this.currentGalleryDescription = '';
            this.currentGalleryCostUnit = '';
            this.currentGalleryCost = '';
        }

    }
});
</script>

<style scoped>

</style>
